import {
  Marker as MarkerComponent,
  InfoWindow as MarkerInfoWindow,
  OverlayView,
  OverlayViewF,
} from '@react-google-maps/api'
import {
  ChargingStatus,
  ChargingStatusStr,
  DeviceType,
} from '@services/constants'
import {
  sumCountByDeviceType,
  useSessionDeviceStatistical,
} from '@services/device'
import { ISession } from '@services/session'
import { imageUrlPrefix } from '@shared/env'
import useVisible from '@shared/innmaxLib/hooks/useVisible'
import { Button } from '@shared/innmaxUI/Form'
import cx from 'classnames'
import React from 'react'
import styled from 'styled-components'
import { IParkingLotMarkerProps } from '../map.types'
import { useInnmaxMap } from '../Provider'
import { useGoogleMap } from './Provider'
import { DeleteWindow } from './Window'

enum MarkerImageType {
  PNG = '.png',
  SVG = '.svg',
}

function markerIcon(options: {
  unavailableCount: number
  availableCount: number
  inUseCount: number
  imageType: MarkerImageType
}) {
  let status = ChargingStatusStr[ChargingStatus.AVAILABLE]
  if (options.availableCount > 0) {
    status = ChargingStatusStr[ChargingStatus.AVAILABLE]
  } else if (options.inUseCount > 0) {
    status = ChargingStatusStr[ChargingStatus.INUSE]
  } else if (options.unavailableCount > 0) {
    status = ChargingStatusStr[ChargingStatus.UNAVAILABLE]
  }

  return {
    icon: `${imageUrlPrefix}/marker/parkingLot/${status}${options.imageType}`,
    focusIcon: `${imageUrlPrefix}/marker/parkingLot/focus-${status}${options.imageType}`,
  }
}

export default function ParkingLotMarker({
  children,
  item,
  focus,
  tooltip = true,
  clusterer,
  // enableClusterer = true,
  deletable = false,
  draggable = false,
  selected = false,
  devicestatisticalBySessionId,
  onLeftClick,
  onRightClick,
  onMouseOver,
  onMouseOut,
  onDrag,
  onDragEnd,
  onDelete,
  onEnter,
  ...others
}: IParkingLotMarkerProps) {
  const markerRef = React.useRef<any>()

  const { getSessionDeepStatistical } = useSessionDeviceStatistical()

  const statistical = getSessionDeepStatistical(
    item,
    devicestatisticalBySessionId
  )

  const { isStreetMode } = useGoogleMap()

  const { focusParkingLot, setFocusParkingLot } = useInnmaxMap()

  const [markerPosition, setMarkerPosition] = React.useState<
    google.maps.LatLng | google.maps.LatLngLiteral
  >()

  const tooltipWindow = useVisible()

  const deleteWindow = useVisible()
  const contextMenuWindow = useVisible()

  const markerVisible = useVisible()

  const icon = markerIcon({
    inUseCount: sumCountByDeviceType(
      statistical,
      'summaryChargingCount',
      DeviceType.CONNECTOR
    ),
    unavailableCount: sumCountByDeviceType(
      statistical,
      'summaryUnavailableCount',
      DeviceType.CONNECTOR
    ),
    availableCount: sumCountByDeviceType(
      statistical,
      'summaryAvailableCount',
      DeviceType.CONNECTOR
    ),
    imageType: MarkerImageType.PNG,
  })

  const className = React.useMemo(
    () => cx({ hidden: clusterer && !markerVisible.visible }),
    [clusterer, markerVisible.visible]
  )

  const handleLeftClick = React.useCallback(
    (e: any) => {
      item && onLeftClick && onLeftClick(item)
    },
    [onLeftClick]
  ) //eslint-disable-line

  const handleRightClick = React.useCallback(
    (e: any) => {
      item && onRightClick && onRightClick()

      if (deletable) {
        deleteWindow.toggle()
      } else {
        contextMenuWindow.toggle()
      }
    },
    [onRightClick]
  ) //eslint-disable-line

  const handleMouseOver = React.useCallback((e: any) => {
    tooltipWindow.open()
  }, []) //eslint-disable-line

  const handleMouseOut = React.useCallback((e: any) => {
    tooltipWindow.close()
  }, []) //eslint-disable-line

  const handleOnDrag = React.useCallback(
    (e: google.maps.MapMouseEvent) => {
      setMarkerPosition(e.latLng?.toJSON())
      onDrag && onDrag()
    },
    [onDrag]
  ) //eslint-disable-line

  const handleOnDragEnd = React.useCallback(
    (e: google.maps.MapMouseEvent) => {
      setMarkerPosition(e.latLng?.toJSON())
      onDragEnd &&
        onDragEnd({
          ...item,
          lat: e?.latLng?.lat() || 0,
          lon: e?.latLng?.lng() || 0,
        } as ISession)
    },
    [onDragEnd]
  ) //eslint-disable-line

  const handleOnMarkerVisibleChanged = React.useCallback(() => {
    if (markerRef) {
      markerRef?.current?.marker?.visible
        ? markerVisible.open()
        : markerVisible.close()
    }
    tooltipWindow.close()
  }, [markerRef]) //eslint-disable-line

  React.useEffect(() => {
    if (item?.lat && item?.lon) {
      setMarkerPosition({
        lat: item?.lat,
        lng: item?.lon,
      })
    }
  }, [item])

  React.useEffect(() => {
    // 關閉刪除window
    document.addEventListener('click', deleteWindow.close)
    return () => document.removeEventListener('click', deleteWindow.close)
  }, []) //eslint-disable-line

  React.useEffect(() => {
    document.addEventListener('click', contextMenuWindow.close)
    return () => document.removeEventListener('click', contextMenuWindow.close)
  }, []) //eslint-disable-line

  if (!markerPosition?.lat || !markerPosition?.lng) {
    return <div />
  }

  return (
    <MarkerComponent
      icon={isStreetMode ? undefined : icon.icon}
      ref={markerRef}
      title={isStreetMode ? item?.name || '' : ''}
      position={markerPosition as google.maps.LatLng}
      clusterer={clusterer}
      animation={focus ? google.maps.Animation.BOUNCE : undefined}
      onDrag={handleOnDrag}
      onDragEnd={handleOnDragEnd}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
      onClick={handleLeftClick}
      onRightClick={handleRightClick}
      onVisibleChanged={handleOnMarkerVisibleChanged}
      zIndex={50}
      opacity={isStreetMode ? 1 : 0}
      draggable={draggable}>
      <OverlayViewF
        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        position={markerPosition as google.maps.LatLng}
        {...others}>
        <>
          {tooltip && tooltipWindow.visible && item?.name && (
            <MarkerInfoWindow
              position={markerPosition}
              options={{
                ...infoboxOptions,
                pixelOffset: new google.maps.Size(-1, -25),
              }}>
              <StyledTooltipWrapper>
                <StyledTootipContent className="notranslate">
                  {item?.name}
                </StyledTootipContent>
                <StyledTooltipArrow></StyledTooltipArrow>
              </StyledTooltipWrapper>
            </MarkerInfoWindow>
          )}
          {deleteWindow.visible && (
            <MarkerInfoWindow
              position={markerPosition}
              options={{
                ...infoboxOptions,
                pixelOffset: new google.maps.Size(100, 13),
              }}>
              <DeleteWindow
                data={item}
                onCancel={deleteWindow.toggle}
                deleteMarker={() => item && onDelete && onDelete(item)}
              />
            </MarkerInfoWindow>
          )}
          {contextMenuWindow.visible && (
            <MarkerInfoWindow
              position={markerPosition}
              options={{
                ...infoboxOptions,
                pixelOffset: new google.maps.Size(100, 18),
              }}>
              <ContextMenu>
                <ButtonStyled
                  block
                  onClick={() => {
                    item && onEnter && onEnter(item)
                    contextMenuWindow.close()
                  }}>
                  進入站點
                </ButtonStyled>
              </ContextMenu>
            </MarkerInfoWindow>
          )}
          <StyledMarkerWrapper className={className}>
            <img src={icon.icon} alt="" />
          </StyledMarkerWrapper>
          {focusParkingLot?.id === item?.id && markerVisible.visible ? (
            <StyleFocusWrapper>
              <img src={icon.focusIcon} alt="" />
            </StyleFocusWrapper>
          ) : null}
        </>
      </OverlayViewF>
    </MarkerComponent>
  )
}

const StyledMarkerWrapper = styled.div`
  translate: -13px -30px;
`

const StyleFocusWrapper = styled.div`
  translate: -16px -185px;
`

const StyledTooltipWrapper = styled.div`
  position: relative;
`

const StyledTootipContent = styled.div`
  background-color: ${p => p.theme.yellow100};
  border-radius: 2px;
  font-size: 13px;
  font-weight: normal;
  padding: 3px 8px;
  min-width: 68px;
  min-height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.2);
`

const StyledTooltipArrow = styled.div`
  background-color: transparent;
  margin: 0 auto;
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 10px solid ${p => p.theme.yellow100};
`

const infoboxOptions = {
  visible: true,
  alignBottom: false,
  disableAutoPan: false,
  enableEventPropagation: false,
  boxClass: 'style-info-box',
  boxStyle: {
    overflow: 'hidden',
  },
}

const ContextMenu = styled.div`
  width: 160px;
  margin-left: 15px;
  padding: 4px 0;
  border-radius: 11px;
`

const ButtonStyled = styled(Button)`
  border: 0px !important;
  color: #fff !important;
  background-color: #5c6365;
  &:hover,
  &:focus {
    background-color: #0486ff;
  }
`
